import { Dialog } from 'primereact/dialog'
import { useTranslation } from 'react-i18next'
import Button from '../../../components/ui/Button'

export interface PreviewData {
  name: string
  groupName: string
  date: string
  time: string
  location: string
  editorName: string
  participants: { fullName: string; employeeId: string; jobTitle: string }[]
  /** Girilen aksiyonlar (önizleme; Atanan Kişi görünür-pasif olduğundan yer almaz). */
  actions: { name: string; description: string; importance: string; deadline: string }[]
  /** Gündem maddeleri (başlık + açıklama). */
  agenda: { name: string; description: string }[]
  /** Prensip kararları (yalnız metin). */
  principles: { name: string }[]
  /** Kararlar (yalnız metin). */
  decisions: { name: string }[]
}

interface Props {
  visible: boolean
  data: PreviewData | null
  publishing: boolean
  downloading: boolean
  onCancel: () => void
  onDownload: () => void
  onPublish: () => void
}

/**
 * Tutanak Önizleme (PDF) — Yayınla öncesi, girilen bilgilerin standart tutanak biçiminde önizlemesi (044).
 * Bizim tasarım dilimizle: belge-kartı içinde temel bilgiler + katılımcılar + içerik bölümleri (Aksiyon,
 * Prensip Kararları, Kararlar, Gündem — analiz 4.3 sırası; girilen kayıtlar gerçek tablolarda). Onaylanınca
 * (Yayınla) tutanak kaydedilir ve PDF üretilir; İndir ile önizleme PDF'i kaydetmeden indirilir.
 */
export default function TutanakPreviewDialog({
  visible,
  data,
  publishing,
  downloading,
  onCancel,
  onDownload,
  onPublish,
}: Readonly<Props>) {
  const { t } = useTranslation()

  const footer = (
    <div className="pop-footer">
      <Button
        variant="secondary"
        icon="pi pi-download"
        label={t('minutes.preview.download')}
        onClick={onDownload}
        loading={downloading}
        disabled={publishing}
      />
      <Button
        icon="pi pi-check"
        label={t('minutes.preview.publish')}
        onClick={onPublish}
        loading={publishing}
        disabled={publishing}
      />
    </div>
  )

  return (
    <Dialog
      visible={visible}
      onHide={onCancel}
      header={t('minutes.preview.title')}
      footer={footer}
      modal
      dismissableMask
      style={{ width: '46rem', maxWidth: '95vw' }}
      className="tp-dialog"
    >
      {data && (
        <div className="tp-doc">
          <h2 className="tp-doc-title">{data.name}</h2>
          <div className="tp-doc-info">
            <span>
              <b>{t('minutes.preview.date')}:</b> {data.date}
            </span>
            <span>
              <b>{t('minutes.preview.time')}:</b> {data.time}
            </span>
            <span>
              <b>{t('minutes.preview.location')}:</b> {data.location}
            </span>
            <span>
              <b>{t('minutes.preview.group')}:</b> {data.groupName}
            </span>
            <span>
              <b>{t('minutes.preview.editor')}:</b> {data.editorName}
            </span>
          </div>

          <div className="tp-section">
            <h3 className="tp-section-title">{t('minutes.preview.participants')}</h3>
            <table className="tp-table">
              <thead>
                <tr>
                  <th>{t('meetings.detail.participants.name')}</th>
                  <th>{t('meetings.detail.participants.sicil')}</th>
                  <th>{t('meetings.detail.participants.jobTitle')}</th>
                </tr>
              </thead>
              <tbody>
                {data.participants.map((p) => (
                  <tr key={p.employeeId}>
                    <td className="tp-strong">{p.fullName}</td>
                    <td>{p.employeeId}</td>
                    <td>{p.jobTitle}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Aksiyonlar — işlevsel (046); girilen aksiyonlar önizlemede. Atanan Kişi görünür-pasif → yok. */}
          {data.actions.length > 0 && (
            <div className="tp-section">
              <h3 className="tp-section-title">{t('minutes.actions.title')}</h3>
              <table className="tp-table">
                <thead>
                  <tr>
                    <th>{t('actions.columns.name')}</th>
                    <th>{t('actions.form.description')}</th>
                    <th>{t('actions.columns.importance')}</th>
                    <th>{t('actions.columns.deadline')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.actions.map((a, i) => (
                    <tr key={i}>
                      <td className="tp-strong">{a.name}</td>
                      <td>{a.description}</td>
                      <td>{a.importance}</td>
                      <td>{a.deadline}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Prensip Kararları — girilen kayıtlar (analiz 4.3 sırası). */}
          {data.principles.length > 0 && (
            <div className="tp-section">
              <h3 className="tp-section-title">{t('minutes.principles.title')}</h3>
              <table className="tp-table">
                <thead>
                  <tr>
                    <th>{t('minuteItems.principles.name')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.principles.map((p, i) => (
                    <tr key={i}>
                      <td>{p.name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Kararlar. */}
          {data.decisions.length > 0 && (
            <div className="tp-section">
              <h3 className="tp-section-title">{t('minutes.decisions.title')}</h3>
              <table className="tp-table">
                <thead>
                  <tr>
                    <th>{t('minuteItems.decisions.name')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.decisions.map((d, i) => (
                    <tr key={i}>
                      <td>{d.name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Gündem — başlık + açıklama. */}
          {data.agenda.length > 0 && (
            <div className="tp-section">
              <h3 className="tp-section-title">{t('minutes.agenda.title')}</h3>
              <table className="tp-table">
                <thead>
                  <tr>
                    <th>{t('minuteItems.agenda.name')}</th>
                    <th>{t('minuteItems.agenda.description')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.agenda.map((a, i) => (
                    <tr key={i}>
                      <td className="tp-strong">{a.name}</td>
                      <td>{a.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="tp-note">{t('minutes.preview.note')}</p>
        </div>
      )}
    </Dialog>
  )
}
